import { create } from 'zustand'

import { executeGet } from '@/services/lukita'
import { captureException } from '@/services/monitoring'

interface CurrentUserDTO {
  admin: boolean
}

interface CurrentUserStoreActions {
  reset: () => void
}

interface CurrentUserStoreLoaders {
  fetchCurrentUser: () => Promise<void>
}

interface CurrentUserStore {
  // Null until lukita answers, so a page can wait instead of guessing "not an admin".
  isAdmin: boolean | null
  actions: CurrentUserStoreActions
  loaders: CurrentUserStoreLoaders
}

// Admin = the lukita `users.admin` flag, the same one zidane checks at sign-in.
export const useCurrentUserStore = create<CurrentUserStore>()(
  (set, _get, store) => ({
    isAdmin: null,
    actions: {
      reset: () => set(store.getInitialState()),
    },
    loaders: {
      fetchCurrentUser: async () => {
        try {
          const response = await executeGet('/users/me')
          const user: CurrentUserDTO = await response.json()
          set({ isAdmin: user.admin })
        } catch (error) {
          // Only admin tools depend on this, so a failed lookup hides them instead of breaking the app.
          captureException(error as Error)
          set({ isAdmin: false })
        }
      },
    },
  }),
)

export const useIsAdmin = () => useCurrentUserStore((state) => state.isAdmin)
export const useCurrentUserActions = () =>
  useCurrentUserStore((state) => state.actions)
export const useCurrentUserLoaders = () =>
  useCurrentUserStore((state) => state.loaders)
